import { MessageTemplate } from "../../entities/message-template.model";
import {
	TableTemplateButtons,
	TableTemplateTag,
} from "../../components/table/table-template.model";

export type MessageTemplateScope = "GLOBAL" | "COACH" | "INSTITUTION" | "COACH_INSTITUTION";

export const getMessageTemplateScope = (template: MessageTemplate): MessageTemplateScope => {
	if (template.coachId && template.institutionId) return "COACH_INSTITUTION";
	if (template.coachId) return "COACH";
	if (template.institutionId) return "INSTITUTION";
	return "GLOBAL";
};

const scopeSeverity = (scope: MessageTemplateScope): TableTemplateTag["severity"] => {
	switch (scope) {
		case "GLOBAL":
			return "info";
		case "COACH":
			return "success";
		case "INSTITUTION":
			return "warning";
		case "COACH_INSTITUTION":
		default:
			return "default";
	}
};

export const buildScopeTag = (template: MessageTemplate): TableTemplateTag => {
	const scope = getMessageTemplateScope(template);
	return {
		value: `MESSAGE_TEMPLATES.SCOPE.${scope}`,
		severity: scopeSeverity(scope),
		rounded: true,
	};
};

/**
 * One row in the nested table shown when expanding a multi-variant group. Each
 * variant gets its own row with the same column shape as the parent table so
 * cells line up under their headers.
 */
export class MessageTemplateVariantRow {
	id: number;
	// Blank `name` column on variant rows — the parent group row already shows
	// the shared name; this slot is kept to align under the parent's NAME header.
	name: string;
	languageCode: string;
	coach: number | null;
	institutionName: string;
	scopeTag: TableTemplateTag;
	actionsTemplate: TableTemplateButtons;

	constructor(params: {
		template: MessageTemplate;
		institutionName: string;
		onEdit: (templateId: number) => void;
		onDelete: (templateId: number, name: string) => void;
	}) {
		const { template, institutionName, onEdit, onDelete } = params;
		this.id = template.id as number;
		this.name = "";
		this.languageCode = template.languageCode ?? "";
		this.coach = template.coachId;
		this.institutionName = institutionName;
		this.scopeTag = buildScopeTag(template);
		this.actionsTemplate = [
			{
				id: "btnEdit",
				icon: "pi pi-pencil",
				className: "p-button-text neutral",
				onClick: () => onEdit(template.id as number),
			},
			{
				id: "btnDelete",
				icon: "pi pi-trash",
				className: "p-button-text neutral",
				onClick: () => onDelete(template.id as number, template.name),
			},
		];
	}
}

/**
 * One row in the message templates list. Each row represents a single template
 * name group — for names with multiple variants (different coach / institution /
 * language) the main row shows only the name + expander, and the expansion
 * lists every variant in the group.
 */
export class MessageTemplateRow {
	id: string;
	name: string;
	languageCode: string;
	coach: number | null;
	institutionName: string;
	scopeTag: TableTemplateTag;
	expandCollapse: boolean;
	actionsTemplate: TableTemplateButtons;
	variants: MessageTemplate[];
	representative: MessageTemplate;

	constructor(params: {
		name: string;
		variants: MessageTemplate[];
		institutionName: string;
		onEdit: (templateId: number) => void;
		onDelete: (templateId: number, name: string) => void;
	}) {
		const { name, variants, institutionName, onEdit, onDelete } = params;
		const representative = variants[0];
		const isExpandable = variants.length > 1;

		this.id = `group-${name}`;
		this.name = name;
		// For expandable (multi-variant) rows blank out the per-variant fields —
		// they only make sense at the variant level inside the expansion.
		this.languageCode = isExpandable ? "" : (representative.languageCode ?? "");
		this.coach = isExpandable ? null : representative.coachId;
		this.institutionName = isExpandable ? "" : institutionName;
		this.scopeTag = buildScopeTag(representative);
		// Single-variant rows have no children to reveal.
		this.expandCollapse = isExpandable;
		// Edit button on the main row only for single-variant groups; expandable
		// rows replace the edit button with the chevron in the same column.
		this.actionsTemplate =
			!isExpandable && representative.id !== null
				? [
						{
							id: "btnEdit",
							icon: "pi pi-pencil",
							className: "p-button-text neutral",
							onClick: () => onEdit(representative.id as number),
						},
						{
							id: "btnDelete",
							icon: "pi pi-trash",
							className: "p-button-text neutral",
							onClick: () =>
								onDelete(representative.id as number, representative.name),
						},
					]
				: [];
		this.variants = variants;
		this.representative = representative;
	}
}
